import { resolveSpeakerRole, type SpeakerRoleMap } from '@/lib/speaker-roles';

/**
 * Who a transcript line is attributed to, as a person reads it — one
 * implementation for the record screen, «Copy» and the markdown export (#889).
 *
 * A Bitrix chat line arrives as `role (id)` — `agent (17732)`,
 * `customer (16206)` — where the id means nothing to a reader. It is replaced
 * by a name ONLY where the record says whose id it is: the operator whose id is
 * the record's `agentId` gets `agentName`; the customer gets `participantName`
 * only when the transcript has exactly one customer id (`customerId`, see
 * `speakerNamesFor`). A call whose cleanup was rejected is served as the ASR
 * source, every label raw (`speaker_0`) — the names categorize-step resolved
 * are written only into the cleaned version (#1866) — so there a label whose
 * side the analysis resolved gets that side's record name, when exactly one
 * label holds the side. A cleaned call is left alone, a `speaker_N` it kept
 * included: categorize-step left that speaker unnamed on purpose. Everything
 * else keeps the label it had before: an uploaded chat puts a NAME in the
 * parentheses, a chat without roles tells two people apart only by their ids.
 */
export interface SpeakerNames {
  speakerRoles?: SpeakerRoleMap;
  agentId?: string | null;
  agentName?: string | null;
  participantName?: string | null;
  /** The transcript's only customer id — set by `speakerNamesFor`. */
  customerId?: string | null;
  /**
   * Raw call label (`callLabelKey`) → the record name it gets — set by
   * `speakerNamesFor` once per transcript, so a label is a lookup per line.
   */
  callLabelNames?: ReadonlyMap<string, string>;
}

type Translate = (key: string, fallback: string) => string;

export type SpeakerSide = 'agent' | 'client' | null;

/** `customer (16206)` → role `customer`, id `16206`; a bare label has no id. */
export function parseSpeaker(speaker: string): { role: string; id: string | null } {
  const trimmed = speaker.trim();
  const match = trimmed.match(/^([^(]+?)\s*(?:\(([^)]*)\))?$/);
  return {
    role: (match?.[1] ?? trimmed).toLowerCase(),
    id: match?.[2] ?? null,
  };
}

const AGENT_ROLES = new Set(['agent', 'operator', 'оператор']);
const CLIENT_ROLES = new Set(['client', 'customer', 'клієнт', 'клиент']);

/** A Bitrix user id; a name in the parentheses is not one. */
const BITRIX_USER_ID = /^\d+$/;

/**
 * CRM stub names for an unnamed lead. The analysis prompt already refuses them
 * as speaker names; the transcript must not print them either.
 */
const PLACEHOLDER_NAME =
  /^(без\s+(имени|імені)|unknown|гость|гість|клиент\s*\d*|клієнт\s*\d*|client\s*\d*)$/i;

/** A diarization label the ASR wrote, before any cleanup renamed it. */
const RAW_CALL_LABEL = /^speaker[_\s-]?\d+$/i;

/** Does the label still need the analysis' roles to be named? (#1866) */
export function isRawCallLabel(speaker: string): boolean {
  return RAW_CALL_LABEL.test(speaker.trim());
}

/** One key for `speaker_0`, `SPEAKER_0` and `speaker_0 ` — the same person. */
function callLabelKey(speaker: string): string {
  return speaker.trim().toLowerCase();
}

/**
 * A CRM name slot routinely holds a phone or an e-mail where a name should be
 * (#1398); the analysis treats such a value as no name, and so does the
 * transcript — printed on every line it would be an identifier, not a person.
 */
const IDENTIFIER_NAME = /@|(?:\d\D*){7}/;

/** The record's name, or null for a CRM stub or an identifier. */
function realName(name: string | null | undefined): string | null {
  const trimmed = name?.trim();
  return trimmed && !PLACEHOLDER_NAME.test(trimmed) && !IDENTIFIER_NAME.test(trimmed)
    ? trimmed
    : null;
}

/**
 * The comparison `namesCollide` makes in workflows-api (#1452, `normalizeSpeaker`
 * in cleanup-integrity): NFKC, case ignored, whitespace collapsed, quotes dropped.
 */
function comparableName(name: string): string {
  return name
    .normalize('NFKC')
    .toLocaleLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/["'`’]/g, '');
}

/**
 * Names for the labels of a call served as the ASR source. Empty unless EVERY
 * label is raw: a cleaned call keeps `speaker_N` only for a speaker
 * categorize-step left unnamed on purpose, and the record must not name it
 * over that decision. A side two labels share names nobody (a conference, a
 * diarization split), and so does a name both sides share (#1452): printed on
 * both, it would erase the distinction the role words kept.
 */
function callLabelNamesFor(
  speakers: readonly string[],
  names: Omit<SpeakerNames, 'customerId' | 'callLabelNames'>
): ReadonlyMap<string, string> {
  const labels = new Set(
    speakers.filter((speaker) => speaker.trim() !== '').map(callLabelKey)
  );
  const result = new Map<string, string>();
  if (labels.size === 0 || ![...labels].every(isRawCallLabel)) return result;

  const agent = realName(names.agentName);
  const client = realName(names.participantName);
  if (agent && client && comparableName(agent) === comparableName(client)) return result;

  for (const [side, name] of [
    ['agent', agent],
    ['client', client],
  ] as const) {
    const onSide = [...labels].filter(
      (label) => resolveSpeakerRole(label, names.speakerRoles) === side
    );
    if (name && onSide.length === 1) result.set(onSide[0]!, name);
  }
  return result;
}

/**
 * The side of the conversation. The role the analysis resolved for a label
 * wins: `speaker_0` reads as an unknown proper name, but the pipeline knows it
 * was the agent.
 */
export function speakerSide(speaker: string, roles?: SpeakerRoleMap): SpeakerSide {
  const mapped = resolveSpeakerRole(speaker, roles);
  if (mapped === 'agent' || mapped === 'client') return mapped;
  const { role } = parseSpeaker(speaker);
  if (AGENT_ROLES.has(role)) return 'agent';
  if (CLIENT_ROLES.has(role)) return 'client';
  return null;
}

/**
 * The names for one transcript: `customerId` is the customer's Bitrix id when
 * the transcript has exactly one. Two customer ids — a legacy chat where every
 * non-operator became a customer, or several connector users — name nobody.
 * The same rule for a call's raw labels: two `speaker_N` on one side (a
 * conference, a diarization split) name nobody on that side.
 */
export function speakerNamesFor(
  speakers: readonly string[],
  names: Omit<SpeakerNames, 'customerId' | 'callLabelNames'>
): SpeakerNames {
  const customerIds = new Set(
    speakers
      .filter((speaker) => speakerSide(speaker, names.speakerRoles) === 'client')
      .map((speaker) => parseSpeaker(speaker).id)
      .filter((id): id is string => id !== null && BITRIX_USER_ID.test(id))
  );
  return {
    ...names,
    customerId: customerIds.size === 1 ? [...customerIds][0]! : null,
    callLabelNames: callLabelNamesFor(speakers, names),
  };
}

export function speakerLabel(speaker: string, names: SpeakerNames, t: Translate): string {
  const side = speakerSide(speaker, names.speakerRoles);
  const { role, id } = parseSpeaker(speaker);

  if (id !== null && BITRIX_USER_ID.test(id)) {
    if (side === 'agent' && names.agentName && id === names.agentId) {
      return names.agentName;
    }
    const participant = realName(names.participantName);
    if (side === 'client' && id === names.customerId && participant) {
      return participant;
    }
  }

  const callName = names.callLabelNames?.get(callLabelKey(speaker));
  if (callName) return callName;

  // Unchanged from before #889: the role word, and the parenthetical kept.
  const mapped = resolveSpeakerRole(speaker, names.speakerRoles);
  if (mapped === 'agent') return t('records.speakerAgent', 'Agent');
  if (mapped === 'client') return t('records.speakerClient', 'Client');
  const suffix = id !== null ? ` (${id})` : '';
  if (AGENT_ROLES.has(role)) return `${t('records.speakerAgent', 'Agent')}${suffix}`;
  if (CLIENT_ROLES.has(role)) return `${t('records.speakerClient', 'Client')}${suffix}`;
  return capitalizeSpeakerName(speaker);
}

/**
 * An unknown speaker is a name: each word capitalized. One rule for the
 * transcript lines and the talk-ratio legend beside them (#1105).
 */
export function capitalizeSpeakerName(speaker: string): string {
  return speaker
    .split(' ')
    .map((word) =>
      word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word
    )
    .join(' ');
}
